// The concurrency-control panel: what strategy the compiler picked for each
// `txn` (pessimistic lock vs. optimistic OCC) and the whole-program memory mode
// (in-place vs. MVCC). This is the LockPlan analysis — the same one the Go
// backend consumes — surfaced for the editor. Clicking a txn jumps to its line.

import type { PlanResult } from "../../src/compiler.js";
import {
  modeSummary,
  strategyBadge,
  strategyClass,
  strategyLabel,
} from "../lockplan-view.js";

interface LockPlanPanelProps {
  plan: PlanResult | null;
  strategy: "occ" | "hybrid";
  onStrategyChange(strategy: "occ" | "hybrid"): void;
  onSelectTxn(line: number): void;
}

export function LockPlanPanel({
  plan,
  strategy,
  onStrategyChange,
  onSelectTxn,
}: LockPlanPanelProps) {
  return (
    <div id="lockplan">
      {/* The switch is the panel's own control, so it sits with the plan rather
          than in the header: a header is a title and a fold. */}
      <div className="lp-strategy" role="group" aria-label="planning strategy">
        {(["occ", "hybrid"] as const).map((s) => (
          <button
            key={s}
            className={strategy === s ? "on" : ""}
            onClick={() => onStrategyChange(s)}
            title={
              s === "occ"
                ? "Every txn optimistic (baseline)."
                : "Infer static footprints; lock where possible."
            }
          >
            {s}
          </button>
        ))}
      </div>
      {plan === null ? (
        <div className="lp-empty">— nothing to analyze yet</div>
      ) : (
        <LockPlanBody plan={plan} onSelectTxn={onSelectTxn} />
      )}
    </div>
  );
}

function LockPlanBody({
  plan,
  onSelectTxn,
}: {
  plan: PlanResult;
  onSelectTxn(line: number): void;
}) {
  const { plan: lp, entries } = plan;
  const mode = modeSummary(lp);
  const lockCount = lp.lockCount;

  return (
    <>
      <div className="lp-status">
        <span className={`lp-mode ${mode.cls}`} title={mode.detail}>
          memory: {mode.label}
        </span>
        <span className="lp-counts">
          {lp.txnCount} txn{lp.txnCount === 1 ? "" : "s"}
          {lp.txnCount > 0 && (
            <>
              {" · "}
              <span className="lp-lock">{lockCount} lock</span>
              {" · "}
              <span className="lp-occ">{lp.txnCount - lockCount} OCC</span>
            </>
          )}
        </span>
      </div>
      {mode.mode === "mvcc" && lp.inPlaceBlockedBy && (
        <div className="lp-blocked">
          in-place blocked by: <b>{lp.inPlaceBlockedBy}</b>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="lp-empty">no transactions in this program</div>
      ) : (
        <ul className="lp-txns">
          {entries.map((e, i) => (
            <li
              key={i}
              className="lp-txn"
              title={strategyLabel(e.strategy)}
              onClick={() => onSelectTxn(e.span.start.line)}
            >
              <span className="lp-badge">{strategyBadge(e.strategy)}</span>
              <span className="lp-loc">L{e.span.start.line}</span>
              <span className={`lp-strat ${strategyClass(e.strategy)}`}>
                {e.strategy.kind === "lock" ? "lock" : "OCC"}
              </span>
              <span className="lp-desc">{strategyLabel(e.strategy)}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
